/**
 * SharedDDNotes Component
 *
 * Display and add collaborative notes in a shared DD.
 */

import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, TextInput, ScrollView } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  MessageSquare,
  Plus,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  ListTodo,
  Lock,
  Send,
  X,
  FileText,
  ChevronDown,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInUp, Layout } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import { useThemeColors } from '@/lib/useThemeColors';
import type { DDNote, NoteCategory, DDDocumentRef } from '@/lib/shared-dd';
import { NOTE_CATEGORIES, formatRelativeTime, canAddNotes } from '@/lib/shared-dd';
import type { CollaboratorRole } from '@/lib/shared-dd';

// ============================================================================
// Types
// ============================================================================

/**
 * Simple document info for the document selector
 */
export interface AvailableDocument {
  id: string;
  name: string;
  type?: string; // e.g., 'pitch_deck', 'financials'
}

interface SharedDDNotesProps {
  notes: DDNote[];
  currentUserId: string;
  currentUserName: string;
  myRole: CollaboratorRole;
  /** Available data room documents for linking */
  availableDocuments?: AvailableDocument[];
  /** Filter to show only notes for a specific document */
  filterByDocument?: string;
  onAddNote: (content: string, category: NoteCategory, isPrivate: boolean, documentRef?: DDDocumentRef) => void;
  onReact: (noteId: string, reactionType: 'agree' | 'disagree' | 'important') => void;
  onDelete?: (noteId: string) => void;
  /** Called when user clicks on a document reference in a note */
  onDocumentPress?: (documentId: string, documentName: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function SharedDDNotes({
  notes,
  currentUserId,
  currentUserName,
  myRole,
  availableDocuments = [],
  filterByDocument,
  onAddNote,
  onReact,
  onDelete,
  onDocumentPress,
}: SharedDDNotesProps) {
  const colors = useThemeColors();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState<NoteCategory>('general');
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<AvailableDocument | null>(null);
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);

  const canNote = canAddNotes(myRole);

  // Filter notes by document if specified
  const filteredNotes = filterByDocument
    ? notes.filter((n) => n.documentRef?.documentId === filterByDocument)
    : notes;

  const getCategoryIcon = (category: NoteCategory) => {
    switch (category) {
      case 'strength':
        return <CheckCircle size={14} color={colors.success} />;
      case 'concern':
        return <AlertTriangle size={14} color={colors.error} />;
      case 'question':
        return <HelpCircle size={14} color={colors.info} />;
      case 'action_item':
        return <ListTodo size={14} color={colors.warning} />;
      default:
        return <MessageSquare size={14} color={colors.text.tertiary} />;
    }
  };

  const getCategoryColor = (category: NoteCategory) => {
    switch (category) {
      case 'strength':
        return colors.traffic.greenLight;
      case 'concern':
        return colors.traffic.redLight;
      case 'question':
        return colors.primary[50];
      case 'action_item':
        return colors.traffic.amberLight;
      default:
        return colors.surface.secondary;
    }
  };

  const handleSubmit = () => {
    if (!newContent.trim()) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    // Build document reference if a document is selected
    const documentRef: DDDocumentRef | undefined = selectedDocument
      ? {
          documentId: selectedDocument.id,
          documentName: selectedDocument.name,
          documentType: selectedDocument.type,
        }
      : undefined;

    onAddNote(newContent.trim(), newCategory, isPrivate, documentRef);
    setNewContent('');
    setNewCategory('general');
    setIsPrivate(false);
    setSelectedDocument(null);
    setShowAddForm(false);
  };

  const categories: { value: NoteCategory; label: string }[] = [
    { value: 'general', label: 'General' },
    { value: 'strength', label: 'Strength' },
    { value: 'concern', label: 'Concern' },
    { value: 'question', label: 'Question' },
    { value: 'action_item', label: 'Action' },
  ];

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.text.primary }]}>
          Notes ({notes.length})
        </Text>
        {canNote && !showAddForm && (
          <AnimatedPressable
            onPress={() => {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              setShowAddForm(true);
            }}
            style={[styles.addButton, { backgroundColor: colors.primary[500] }]}
          >
            <Plus size={16} color="#fff" />
            <Text style={styles.addButtonText}>Add Note</Text>
          </AnimatedPressable>
        )}
      </View>

      {/* Add Note Form */}
      {showAddForm && (
        <Animated.View
          entering={FadeInUp.duration(200)}
          style={[
            styles.addForm,
            { backgroundColor: colors.surface.primary, borderColor: colors.border.light },
          ]}
        >
          {/* Category Selector */}
          <View style={styles.categoryRow}>
            {categories.map((cat) => (
              <AnimatedPressable
                key={cat.value}
                onPress={() => setNewCategory(cat.value)}
                style={[
                  styles.categoryPill,
                  {
                    backgroundColor:
                      newCategory === cat.value
                        ? getCategoryColor(cat.value)
                        : colors.surface.secondary,
                    borderColor:
                      newCategory === cat.value
                        ? colors.primary[300]
                        : colors.border.light,
                  },
                ]}
              >
                {getCategoryIcon(cat.value)}
                <Text
                  style={[
                    styles.categoryText,
                    { color: colors.text.secondary },
                  ]}
                >
                  {cat.label}
                </Text>
              </AnimatedPressable>
            ))}
          </View>

          {/* Document Selector */}
          {availableDocuments.length > 0 && (
            <View style={styles.documentSelectorContainer}>
              <AnimatedPressable
                onPress={() => setShowDocumentPicker(!showDocumentPicker)}
                style={[
                  styles.documentSelector,
                  {
                    backgroundColor: selectedDocument
                      ? colors.primary[50]
                      : colors.surface.secondary,
                    borderColor: selectedDocument
                      ? colors.primary[300]
                      : colors.border.light,
                  },
                ]}
              >
                <FileText
                  size={14}
                  color={selectedDocument ? colors.primary[600] : colors.text.tertiary}
                />
                <Text
                  style={[
                    styles.documentSelectorText,
                    {
                      color: selectedDocument
                        ? colors.primary[600]
                        : colors.text.tertiary,
                    },
                  ]}
                  numberOfLines={1}
                >
                  {selectedDocument ? selectedDocument.name : 'Link to document (optional)'}
                </Text>
                <ChevronDown
                  size={14}
                  color={selectedDocument ? colors.primary[600] : colors.text.tertiary}
                />
              </AnimatedPressable>

              {showDocumentPicker && (
                <Animated.View
                  entering={FadeIn.duration(150)}
                  style={[
                    styles.documentPickerDropdown,
                    {
                      backgroundColor: colors.surface.primary,
                      borderColor: colors.border.light,
                    },
                  ]}
                >
                  <ScrollView style={styles.documentPickerScroll} nestedScrollEnabled>
                    {/* Clear selection option */}
                    <AnimatedPressable
                      onPress={() => {
                        setSelectedDocument(null);
                        setShowDocumentPicker(false);
                      }}
                      style={[
                        styles.documentOption,
                        { borderBottomColor: colors.border.light },
                      ]}
                    >
                      <Text style={[styles.documentOptionText, { color: colors.text.tertiary }]}>
                        No document
                      </Text>
                    </AnimatedPressable>

                    {availableDocuments.map((doc) => (
                      <AnimatedPressable
                        key={doc.id}
                        onPress={() => {
                          setSelectedDocument(doc);
                          setShowDocumentPicker(false);
                        }}
                        style={[
                          styles.documentOption,
                          {
                            borderBottomColor: colors.border.light,
                            backgroundColor:
                              selectedDocument?.id === doc.id
                                ? colors.primary[50]
                                : 'transparent',
                          },
                        ]}
                      >
                        <FileText size={14} color={colors.text.secondary} />
                        <View style={styles.documentOptionInfo}>
                          <Text
                            style={[styles.documentOptionText, { color: colors.text.primary }]}
                            numberOfLines={1}
                          >
                            {doc.name}
                          </Text>
                          {doc.type && (
                            <Text
                              style={[styles.documentOptionType, { color: colors.text.tertiary }]}
                            >
                              {doc.type.replace(/_/g, ' ')}
                            </Text>
                          )}
                        </View>
                      </AnimatedPressable>
                    ))}
                  </ScrollView>
                </Animated.View>
              )}
            </View>
          )}

          {/* Content Input */}
          <TextInput
            style={[
              styles.input,
              { backgroundColor: colors.surface.secondary, color: colors.text.primary },
            ]}
            placeholder="Write your note..."
            placeholderTextColor={colors.text.tertiary}
            value={newContent}
            onChangeText={setNewContent}
            multiline
            numberOfLines={3}
            textAlignVertical="top"
          />

          {/* Options & Actions */}
          <View style={styles.formActions}>
            <AnimatedPressable
              onPress={() => setIsPrivate(!isPrivate)}
              style={[
                styles.privateToggle,
                {
                  backgroundColor: isPrivate
                    ? colors.primary[50]
                    : colors.surface.secondary,
                },
              ]}
            >
              <Lock
                size={14}
                color={isPrivate ? colors.primary[600] : colors.text.tertiary}
              />
              <Text
                style={[
                  styles.privateText,
                  { color: isPrivate ? colors.primary[600] : colors.text.tertiary },
                ]}
              >
                Private
              </Text>
            </AnimatedPressable>

            <View style={styles.formButtons}>
              <AnimatedPressable
                onPress={() => setShowAddForm(false)}
                style={[styles.cancelButton, { backgroundColor: colors.surface.secondary }]}
              >
                <Text style={[styles.cancelText, { color: colors.text.secondary }]}>
                  Cancel
                </Text>
              </AnimatedPressable>
              <AnimatedPressable
                onPress={handleSubmit}
                disabled={!newContent.trim()}
                style={[
                  styles.submitButton,
                  {
                    backgroundColor: newContent.trim()
                      ? colors.primary[500]
                      : colors.surface.secondary,
                  },
                ]}
              >
                <Send
                  size={14}
                  color={newContent.trim() ? '#fff' : colors.text.tertiary}
                />
                <Text
                  style={[
                    styles.submitText,
                    { color: newContent.trim() ? '#fff' : colors.text.tertiary },
                  ]}
                >
                  Post
                </Text>
              </AnimatedPressable>
            </View>
          </View>
        </Animated.View>
      )}

      {/* Notes List */}
      <View style={styles.notesList}>
        {filteredNotes.length === 0 ? (
          <View style={styles.emptyState}>
            <MessageSquare size={32} color={colors.text.tertiary} />
            <Text style={[styles.emptyText, { color: colors.text.tertiary }]}>
              {filterByDocument ? 'No notes for this document' : 'No notes yet'}
            </Text>
          </View>
        ) : (
          filteredNotes.map((note, index) => (
            <Animated.View
              key={note.id}
              entering={FadeIn.delay(index * 30).duration(200)}
              layout={Layout.springify()}
            >
              <View
                style={[
                  styles.noteCard,
                  {
                    backgroundColor: getCategoryColor(note.category),
                    borderColor: colors.border.light,
                  },
                ]}
              >
                {/* Note Header */}
                <View style={styles.noteHeader}>
                  <View style={styles.noteAuthor}>
                    {getCategoryIcon(note.category)}
                    <Text style={[styles.authorName, { color: colors.text.primary }]}>
                      {note.authorId === currentUserId ? 'You' : note.authorName}
                    </Text>
                    {note.isPrivate && (
                      <View style={[styles.privateBadge, { backgroundColor: colors.surface.secondary }]}>
                        <Lock size={10} color={colors.text.tertiary} />
                      </View>
                    )}
                  </View>
                  <Text style={[styles.noteTime, { color: colors.text.tertiary }]}>
                    {formatRelativeTime(note.createdAt)}
                  </Text>
                </View>

                {/* Document Reference */}
                {note.documentRef && (
                  <AnimatedPressable
                    onPress={() => {
                      if (onDocumentPress && note.documentRef) {
                        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                        onDocumentPress(note.documentRef.documentId, note.documentRef.documentName);
                      }
                    }}
                    style={[
                      styles.noteDocumentRef,
                      {
                        backgroundColor: colors.surface.secondary,
                        opacity: onDocumentPress ? 1 : 0.7,
                      },
                    ]}
                  >
                    <FileText size={12} color={colors.primary[600]} />
                    <Text
                      style={[styles.noteDocumentName, { color: colors.primary[600] }]}
                      numberOfLines={1}
                    >
                      {note.documentRef.documentName}
                    </Text>
                    {onDocumentPress && (
                      <ChevronDown size={10} color={colors.primary[600]} style={{ transform: [{ rotate: '-90deg' }] }} />
                    )}
                  </AnimatedPressable>
                )}

                {/* Note Content */}
                <Text style={[styles.noteContent, { color: colors.text.primary }]}>
                  {note.content}
                </Text>

                {/* Reactions */}
                <View style={styles.reactions}>
                  <AnimatedPressable
                    onPress={() => onReact(note.id, 'agree')}
                    style={[
                      styles.reactionButton,
                      {
                        backgroundColor: note.reactions?.some(
                          (r) => r.investorId === currentUserId && r.type === 'agree'
                        )
                          ? colors.traffic.greenLight
                          : colors.surface.secondary,
                      },
                    ]}
                  >
                    <ThumbsUp size={12} color={colors.success} />
                    <Text style={[styles.reactionCount, { color: colors.text.secondary }]}>
                      {note.reactions?.filter((r) => r.type === 'agree').length || 0}
                    </Text>
                  </AnimatedPressable>

                  <AnimatedPressable
                    onPress={() => onReact(note.id, 'disagree')}
                    style={[
                      styles.reactionButton,
                      {
                        backgroundColor: note.reactions?.some(
                          (r) => r.investorId === currentUserId && r.type === 'disagree'
                        )
                          ? colors.traffic.redLight
                          : colors.surface.secondary,
                      },
                    ]}
                  >
                    <ThumbsDown size={12} color={colors.error} />
                    <Text style={[styles.reactionCount, { color: colors.text.secondary }]}>
                      {note.reactions?.filter((r) => r.type === 'disagree').length || 0}
                    </Text>
                  </AnimatedPressable>

                  <AnimatedPressable
                    onPress={() => onReact(note.id, 'important')}
                    style={[
                      styles.reactionButton,
                      {
                        backgroundColor: note.reactions?.some(
                          (r) => r.investorId === currentUserId && r.type === 'important'
                        )
                          ? colors.traffic.amberLight
                          : colors.surface.secondary,
                      },
                    ]}
                  >
                    <AlertTriangle size={12} color={colors.warning} />
                    <Text style={[styles.reactionCount, { color: colors.text.secondary }]}>
                      {note.reactions?.filter((r) => r.type === 'important').length || 0}
                    </Text>
                  </AnimatedPressable>

                  {/* Delete (own notes only) */}
                  {note.authorId === currentUserId && onDelete && (
                    <AnimatedPressable
                      onPress={() => onDelete(note.id)}
                      style={[styles.deleteButton, { marginLeft: 'auto' }]}
                    >
                      <X size={14} color={colors.text.tertiary} />
                    </AnimatedPressable>
                  )}
                </View>
              </View>
            </Animated.View>
          ))
        )}
      </View>
    </View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  addButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#fff',
  },
  addForm: {
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    gap: 12,
  },
  categoryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  categoryPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
  },
  categoryText: {
    fontSize: 12,
    fontWeight: '500',
  },
  input: {
    minHeight: 80,
    padding: 12,
    borderRadius: 10,
    fontSize: 14,
  },
  formActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  privateToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  privateText: {
    fontSize: 12,
    fontWeight: '500',
  },
  formButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  cancelButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  cancelText: {
    fontSize: 13,
    fontWeight: '500',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  submitText: {
    fontSize: 13,
    fontWeight: '600',
  },
  notesList: {
    gap: 10,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  noteCard: {
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 8,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  noteAuthor: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  authorName: {
    fontSize: 13,
    fontWeight: '600',
  },
  privateBadge: {
    padding: 3,
    borderRadius: 4,
  },
  noteTime: {
    fontSize: 11,
  },
  noteContent: {
    fontSize: 14,
    lineHeight: 20,
  },
  reactions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  reactionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  reactionCount: {
    fontSize: 11,
    fontWeight: '500',
  },
  deleteButton: {
    padding: 4,
  },
  // Document selector styles
  documentSelectorContainer: {
    position: 'relative',
    zIndex: 10,
  },
  documentSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
  documentSelectorText: {
    flex: 1,
    fontSize: 13,
  },
  documentPickerDropdown: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    borderRadius: 10,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
    maxHeight: 180,
    overflow: 'hidden',
  },
  documentPickerScroll: {
    flexGrow: 0,
  },
  documentOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  documentOptionInfo: {
    flex: 1,
  },
  documentOptionText: {
    fontSize: 13,
  },
  documentOptionType: {
    fontSize: 11,
    marginTop: 2,
    textTransform: 'capitalize',
  },
  // Note document reference styles
  noteDocumentRef: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  noteDocumentName: {
    fontSize: 11,
    fontWeight: '500',
  },
});
